import { Controller } from "@hotwired/stimulus";
import { animate, spring } from "motion";
import type {
  MotionKeyframesDefinition,
  AnimationOptionsWithOverrides,
  SpringOptions,
} from "motion";

const filterInitialKeyframes = (
  initial: MotionKeyframesDefinition,
  selected: MotionKeyframesDefinition
) => {
  const filtered = Object.fromEntries(
    Object.keys(selected)
      .filter((key) => key in initial)
      .map((key) => [key, initial[key as never]])
  );
  return filtered as MotionKeyframesDefinition;
};

export default class extends Controller<HTMLFormElement> {
  static values = {
    initialKeyframes: Object,
    keyframes: Object,
    options: Object,
    hoverKeyframes: Object,
    hoverOptions: Object,
    pressKeyframes: Object,
    pressOptions: Object,
    springOptions: Object,
  };

  declare initialKeyframesValue: MotionKeyframesDefinition;
  declare readonly hasInitialKeyframesValue: boolean;
  declare keyframesValue: MotionKeyframesDefinition;
  declare readonly hasKeyframesValue: boolean;
  declare optionsValue: AnimationOptionsWithOverrides;
  declare readonly hasOptionsValue: boolean;
  declare hoverKeyframesValue: MotionKeyframesDefinition;
  declare readonly hasHoverKeyframesValue: boolean;
  declare hoverOptionsValue: AnimationOptionsWithOverrides;
  declare readonly hasHoverOptionsValue: boolean;
  declare pressKeyframesValue: MotionKeyframesDefinition;
  declare readonly hasPressKeyframesValue: boolean;
  declare pressOptionsValue: AnimationOptionsWithOverrides;
  declare readonly hasPressOptionsValue: boolean;
  declare springOptionsValue: SpringOptions;
  declare readonly hasSpringOptionsValue: boolean;

  start() {
    if (this.keyframesValue) {
      animate(this.element, this.keyframesValue, {
        easing: spring(this.springOptionsValue),
        ...this.optionsValue,
      });
    }
  }

  cancel() {
    animate(
      this.element,
      filterInitialKeyframes(this.initialKeyframesValue, this.keyframesValue),
      { easing: spring(this.springOptionsValue) }
    );
  }

  connect(): void {
    document.addEventListener("turbo:before-cache", this.reset);
    this.element.addEventListener("pointerenter", this.enableHover);
    this.element.addEventListener("pointerleave", this.disableHover);
    this.element.addEventListener("pointerdown", this.onPointerDown);
    if (this.initialKeyframesValue) {
      animate(this.element, this.initialKeyframesValue, {
        duration: 0,
      });
    }
  }

  disconnect(): void {
    document.removeEventListener("turbo:before-cache", this.reset);
    this.element.removeEventListener("pointerenter", this.enableHover);
    this.element.removeEventListener("pointerleave", this.disableHover);
    this.element.removeEventListener("pointerdown", this.onPointerDown);
    window.removeEventListener("pointerup", this.onPointerUp);
  }

  reset = () => {
    const remove: string[] = [];
    for (const name of this.element.style) {
      if (name === "transform" || name.startsWith("--motion")) {
        remove.push(name);
      }
    }
    for (const name of remove) {
      this.element.style.removeProperty(name);
    }
  };

  onPointerDown = () => {
    this.enablePress();
    window.addEventListener("pointerup", this.onPointerUp);
  };

  onPointerUp = () => {
    this.disablePress();
    window.removeEventListener("pointerup", this.onPointerUp);
  };

  enableHover = () => {
    if (this.hoverKeyframesValue) {
      animate(this.element, this.hoverKeyframesValue, {
        easing: spring(this.springOptionsValue),
        ...this.hoverOptionsValue,
      });
    }
  };

  disableHover = () => {
    if (this.hoverKeyframesValue) {
      animate(
        this.element,
        filterInitialKeyframes(
          this.initialKeyframesValue,
          this.hoverKeyframesValue
        ),
        { easing: spring(this.springOptionsValue) }
      );
    }
  };

  enablePress = async () => {
    if (this.pressKeyframesValue) {
      animate(this.element, this.pressKeyframesValue, {
        easing: spring(this.springOptionsValue),
        ...this.pressOptionsValue,
      });
    }
  };

  disablePress = () => {
    // presses should not be able to be disabled
  };
}
